"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { FiInfo } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import {
  HiOutlineBookOpen,
  HiOutlineMicrophone,
  HiOutlinePencilAlt,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MdOutlineChat } from "react-icons/md";
import { IoGameControllerOutline } from "react-icons/io5";
import { TbCategory2 } from "react-icons/tb";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import {
  areAnagramItemsSaveReady,
  hasAtLeastOneMedia,
} from "../shared/mediaValidation";
import anagramAPI from "../../../services/anagram/anagramAPI";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import { MEDIA_BTN_DELETE_MD } from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import { GamePreviewModal } from "../shared/GamePreviewModal";
import {
  createAnagramItemsGameSnapshot,
  getCanGamePreview,
  getShouldConfirmLeaveGameEditor,
  patchSavedAnagramItemsSnapshotAfterItemRemoved,
  patchSavedAnagramItemsSnapshotAfterMediaDeleted,
  PREVIEW_BUTTON_DISABLED_TITLE,
  GAME_PREVIEW_BUTTON_CLASS,
  PREVIEW_UNSAVED_MESSAGE,
  PREVIEW_UNSAVED_TITLE,
  useHasUnsavedGameFormChanges,
} from "../shared/gameFormSnapshot";
import { useReportGameEditorLeaveGuard } from "../shared/GameEditorLeaveGuard";
import {
  anagramItemFieldTarget,
  createGameFieldErrorPopUp,
  getGameFieldBorderClass,
  type GameStatusPopUpState,
  useGameFieldErrorHighlight,
} from "../shared/fieldErrorHighlight";

type AnagramItem = {
  id: number;
  no: number;
  word: string;
  swapWord: string[];
  imageWordFileName: string;
  imageWordFile: File | null;
  imageWordUrl: string | null;
  audioWordFileName: string;
  audioWordFile: File | null;
  audioWordUrl: string | null;
  imageSwapFileName: string;
  imageSwapFile: File | null;
  imageSwapUrl: string | null;
  audioSwapFileName: string;
  audioSwapFile: File | null;
  audioSwapUrl: string | null;
  hasHint: boolean;
  hintText: string;
  hintImageFileName: string;
  hintImageFile: File | null;
  hintImageUrl: string | null;
  hintAudioFileName: string;
  hintAudioFile: File | null;
  hintAudioUrl: string | null;
};

type UploadTarget = {
  key: string;
  file: File;
  fileName: string;
  sectionName: string;
  refId: string;
};

type GameSkills = {
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
  skill_listening_speaking: false,
  skill_lang_nature: false,
  skill_reading: false,
  skill_writing: false,
};

type GameAudios = {
  audio_question: boolean;
  audio_choice: boolean;
  audio_assistant_voice: boolean;
  audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

const DEFAULT_GAME_AUDIOS: GameAudios = {
  audio_question: false,
  audio_choice: false,
  audio_assistant_voice: false,
  audio_background: false,
};

type FindTheAnagramProps = {
  createdGameData?: {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string | null;
    status?: boolean | "true" | "false" | number | string | null;
  } | null;
  initialGameDetail?: {
    id_game_info?: number | string;
    game_info?: {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    };
    questions?: Array<{
      id?: number;
      no?: number;
      word?: string;
      question?: string;
      swap_word?: string[] | string;
      answer?: string;
      hint?: string | null;
      image_word?: string | null;
      sound_word?: string | null;
      image_swap?: string | null;
      sound_swap?: string | null;
      image_swap_word?: string | null;
      sound_swap_word?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    }>;
    anagrams?: Array<{
      id?: number;
      no?: number;
      word?: string;
      question?: string;
      swap_word?: string[] | string;
      answer?: string;
      hint?: string | null;
      image_word?: string | null;
      sound_word?: string | null;
      image_swap?: string | null;
      sound_swap?: string | null;
      image_swap_word?: string | null;
      sound_swap_word?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    }>;
  } | null;
  onCompleted?: () => void;
};

const MIN_ITEMS = 2;

type AnagramHardDeleteField =
  | "sound_word"
  | "sound_swap_word"
  | "image_word"
  | "image_swap_word"
  | "hint"
  | "sound_hint"
  | "image_hint";

type PendingMediaDelete = {
  itemId: number;
  field: AnagramHardDeleteField;
};

function getIncomingAnagramRows(
  detail: FindTheAnagramProps["initialGameDetail"],
): unknown[] {
  if (!detail) {
    return [];
  }

  const detailRecord = detail as Record<string, unknown>;
  if (Array.isArray(detailRecord.questions)) {
    return detailRecord.questions as unknown[];
  }
  if (Array.isArray(detailRecord.anagrams)) {
    return detailRecord.anagrams as unknown[];
  }

  return [];
}

function hasPersistedAnagramRows(
  detail: FindTheAnagramProps["initialGameDetail"],
): boolean {
  return getIncomingAnagramRows(detail).length > 0;
}

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return "";
  }
  return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
  if (!path) {
    return "";
  }

  const cleanPath = String(path);
  const bySlash = cleanPath.split("/");
  const candidate = bySlash[bySlash.length - 1] || "";
  return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const raw = String(path).trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

const thaiSegmenter =
  typeof Intl !== "undefined" && "Segmenter" in Intl
    ? new Intl.Segmenter("th-TH", { granularity: "grapheme" })
    : null;

function buildSwapWord(value: string): string[] {
  const raw = String(value ?? "").trim();
  if (!raw) return [];

  if (thaiSegmenter) {
    return Array.from(thaiSegmenter.segment(raw))
      .map((s) => s.segment)
      .filter((s) => s.trim() !== "");
  }

  return Array.from(raw).filter((s) => s.trim() !== "");
}

function buildWord(value: string): string {
  return String(value ?? "")
    .replace(/\s+/g, "")
    .trim();
}

const ANAGRAM_WORD_MIN_LENGTH = 2;

function getAnagramWordValidationError(
  sourceItems: AnagramItem[],
  labels: {
    minItemsRequired: string;
    wordRequiredByOrder: (no: number) => string;
    wordMinLengthByOrder: (no: number, count: number) => string;
  },
): string | null {
  if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
    return labels.minItemsRequired;
  }

  for (let index = 0; index < sourceItems.length; index += 1) {
    const no = index + 1;
    const normalizedWord = buildWord(sourceItems[index].word);

    if (!normalizedWord) {
      return labels.wordRequiredByOrder(no);
    }

    if (normalizedWord.length < ANAGRAM_WORD_MIN_LENGTH) {
      return labels.wordMinLengthByOrder(no, ANAGRAM_WORD_MIN_LENGTH);
    }
  }

  return null;
}

function normalizeSwapWord(value: unknown, fallbackWord: string): string[] {
  if (Array.isArray(value)) {
    const normalizedEntries = value
      .map((entry) => String(entry))
      .filter((entry) => entry.trim() !== "");

    const joined = normalizedEntries.join("").trim();
    if (joined.startsWith("[") || joined.startsWith('"[')) {
      return normalizeSwapWord(joined, fallbackWord);
    }

    return normalizedEntries;
  }

  if (typeof value === "string") {
    let current = value.trim();

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const looksLikeJsonArrayString =
        current.startsWith("[") || current.startsWith('"[');
      if (!looksLikeJsonArrayString) {
        break;
      }

      try {
        const parsed = JSON.parse(current) as unknown;
        if (Array.isArray(parsed)) {
          return parsed
            .map((entry) => String(entry))
            .filter((entry) => entry.trim() !== "");
        }
        if (typeof parsed === "string") {
          current = parsed.trim();
          continue;
        }
        break;
      } catch {
        break;
      }
    }
  }

  return buildSwapWord(buildWord(fallbackWord));
}

function normalizeSkillBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return null;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

function normalizeExpectedTime(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return "0";
  }
  return String(Math.floor(parsed));
}

function getPublishButtonClass(published: boolean): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75";

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

function renderPublishStatusContent(
  published: boolean,
  labels: { publish: string; unpublish: string },
  iconSize = 16,
) {
  if (published) {
    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#16A34A" />
        </span>
        <span className="text-[#16A34A]">{labels.publish}</span>
      </>
    );
  }

  return (
    <>
      <span className="inline-flex shrink-0 items-center justify-center">
        <BiWorld size={iconSize} color="#3b3b3b" />
      </span>
      <span className="text-[#3b3b3b]">{labels.unpublish}</span>
    </>
  );
}

function getStatusToggleDialogContent(
  currentStatus: boolean,
  labels: {
    confirmPublish: string;
    publishPrompt: string;
    published: string;
    publishedMessage: string;
    confirmUnpublish: string;
    unpublishPrompt: string;
    unpublished: string;
    unpublishedMessage: string;
  },
): StatusToggleDialogContent {
  const nextStatus = !currentStatus;

  if (nextStatus) {
    return {
      confirmTitle: labels.confirmPublish,
      confirmMessage: labels.publishPrompt,
      successTitle: labels.published,
      successMessage: labels.publishedMessage,
    };
  }

  return {
    confirmTitle: labels.confirmUnpublish,
    confirmMessage: labels.unpublishPrompt,
    successTitle: labels.unpublished,
    successMessage: labels.unpublishedMessage,
  };
}

function normalizeGameSkillsOrNull(
  value: Partial<Record<GameSkillKey, unknown>>,
) {
  const listening = normalizeSkillBoolean(value.skill_listening_speaking);
  const langNature = normalizeSkillBoolean(value.skill_lang_nature);
  const reading = normalizeSkillBoolean(value.skill_reading);
  const writing = normalizeSkillBoolean(value.skill_writing);

  if (
    listening === null ||
    langNature === null ||
    reading === null ||
    writing === null
  ) {
    return null;
  }

  return {
    skill_listening_speaking: listening,
    skill_lang_nature: langNature,
    skill_reading: reading,
    skill_writing: writing,
  };
}

function areGameSkillsEqual(left: GameSkills, right: GameSkills) {
  return (
    left.skill_listening_speaking === right.skill_listening_speaking &&
    left.skill_lang_nature === right.skill_lang_nature &&
    left.skill_reading === right.skill_reading &&
    left.skill_writing === right.skill_writing
  );
}

function extractGameSkillsFromUnknown(
  value: Partial<Record<GameSkillKey, unknown>>,
): GameSkills {
  const normalized = normalizeGameSkillsOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function extractGameSkillsFromRow(row: Record<string, unknown>) {
  return normalizeGameSkillsOrNull({
    skill_listening_speaking: row.skill_listening_speaking,
    skill_lang_nature: row.skill_lang_nature,
    skill_reading: row.skill_reading,
    skill_writing: row.skill_writing,
  });
}

function normalizeGameAudiosOrNull(
  value: Partial<Record<GameAudiosKey, unknown>>,
) {
  const audioQuestion = normalizeSkillBoolean(value.audio_question);
  const audioChoice = normalizeSkillBoolean(value.audio_choice);
  const audioAssistantVoice = normalizeSkillBoolean(
    value.audio_assistant_voice,
  );
  const audioBackground = normalizeSkillBoolean(value.audio_background);

  if (
    audioQuestion === null ||
    audioChoice === null ||
    audioAssistantVoice === null ||
    audioBackground === null
  ) {
    return null;
  }

  return {
    audio_question: audioQuestion,
    audio_choice: audioChoice,
    audio_assistant_voice: audioAssistantVoice,
    audio_background: audioBackground,
  };
}

function areGameAudiosEqual(left: GameAudios, right: GameAudios) {
  return (
    left.audio_question === right.audio_question &&
    left.audio_choice === right.audio_choice &&
    left.audio_assistant_voice === right.audio_assistant_voice &&
    left.audio_background === right.audio_background
  );
}

function extractGameAudiosFromUnknown(
  value: Partial<Record<GameAudiosKey, unknown>>,
): GameAudios {
  const normalized = normalizeGameAudiosOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_AUDIOS };
}

function extractGameAudiosFromRow(row: Record<string, unknown>) {
  return normalizeGameAudiosOrNull({
    audio_question: row.audio_question,
    audio_choice: row.audio_choice,
    audio_assistant_voice: row.audio_assistant_voice,
    audio_background: row.audio_background,
  });
}

function createEmptyAnagramItem(id: number, no: number): AnagramItem {
  return {
    id,
    no,
    word: "",
    swapWord: [],
    imageWordFileName: "",
    imageWordFile: null,
    imageWordUrl: null,
    audioWordFileName: "",
    audioWordFile: null,
    audioWordUrl: null,
    imageSwapFileName: "",
    imageSwapFile: null,
    imageSwapUrl: null,
    audioSwapFileName: "",
    audioSwapFile: null,
    audioSwapUrl: null,
    hasHint: false,
    hintText: "",
    hintImageFileName: "",
    hintImageFile: null,
    hintImageUrl: null,
    hintAudioFileName: "",
    hintAudioFile: null,
    hintAudioUrl: null,
  };
}

export default function FindTheAnagram({
  createdGameData,
  initialGameDetail,
  onCompleted,
}: FindTheAnagramProps) {
  const tAnagram = useTranslations("anagram");
  const tMain = useTranslations("main");
  const nextAnagramIdRef = useRef(6);
  const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
  const initialSuggestionRef = useRef("");
  const initialDescriptionRef = useRef("");
  const initialAnagramIdsRef = useRef<Set<number>>(new Set());
  const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });
  const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });
  const savedFormSnapshotRef = useRef<ReturnType<
    typeof createAnagramItemsGameSnapshot
  > | null>(null);

  const [exerciseName, setExerciseName] = useState(
    createdGameData?.exercise_name ?? "",
  );
  const [expectedTime, setExpectedTime] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState<number | null>(
    null,
  );
  const [isUpdateMode, setIsUpdateMode] = useState(
    hasPersistedAnagramRows(initialGameDetail),
  );
  const [isPublished, setIsPublished] = useState(
    normalizeStatus(
      initialGameDetail?.game_info?.status ?? createdGameData?.status ?? false,
    ),
  );
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [statusPopUp, setStatusPopUp] = useState<GameStatusPopUpState | null>(
    null,
  );
  const {
    fieldErrorHighlightKey,
    isFieldError,
    clearFieldErrorHighlight,
    openStatusPopUp,
    closeStatusPopUp,
  } = useGameFieldErrorHighlight();
  const [pendingMediaDelete, setPendingMediaDelete] =
    useState<PendingMediaDelete | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [gameSkills, setGameSkills] = useState<GameSkills>({
    ...DEFAULT_GAME_SKILLS,
  });
  const [gameAudios, setGameAudios] = useState<GameAudios>({
    ...DEFAULT_GAME_AUDIOS,
  });
  const [items, setItems] = useState<AnagramItem[]>([
    createEmptyAnagramItem(1, 1),
    createEmptyAnagramItem(2, 2),
    // createEmptyAnagramItem(3, 3),
    // createEmptyAnagramItem(4, 4),
    // createEmptyAnagramItem(5, 5),
  ]);
  const canRemoveItem = items.length > MIN_ITEMS;

  const previewUuid = String(
    initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? "",
  ).trim();
  useEffect(() => {
    setExerciseName(createdGameData?.exercise_name ?? "");
    setIsPublished(normalizeStatus(createdGameData?.status ?? false));
    initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
  }, [createdGameData?.exercise_name, createdGameData?.status]);

  useEffect(() => {
    const gameInfoId =
      initialGameDetail?.game_info?.id ??
      initialGameDetail?.id_game_info ??
      null;
    if (gameInfoId) {
      return;
    }

    const incomingDescription = String(createdGameData?.description ?? "");
    setDescription(incomingDescription);
    initialDescriptionRef.current = incomingDescription;
  }, [
    createdGameData?.description,
    initialGameDetail?.game_info?.id,
    initialGameDetail?.id_game_info,
  ]);

  const applyLoadedGameDetail = (
    detail: FindTheAnagramProps["initialGameDetail"],
  ) => {
    const detailRecord = (detail ?? {}) as Record<string, unknown>;
    const gameInfo = ((detailRecord.game_info as
      | Record<string, unknown>
      | undefined) ?? {}) as {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    };
    const incomingRows = getIncomingAnagramRows(detail);
    const resolvedGameId = gameInfo.id ?? detailRecord.id_game_info;

    if (!resolvedGameId && incomingRows.length === 0) {
      initialAnagramIdsRef.current = new Set();
      initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
      initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
      setGameSkills({ ...DEFAULT_GAME_SKILLS });
      setGameAudios({ ...DEFAULT_GAME_AUDIOS });
      setExpectedTime("");
      setIsUpdateMode(false);
      savedFormSnapshotRef.current = createAnagramItemsGameSnapshot(
        createdGameData?.exercise_name ?? "",
        "",
        String(createdGameData?.description ?? ""),
        { ...DEFAULT_GAME_SKILLS },
        { ...DEFAULT_GAME_AUDIOS },
        [createEmptyAnagramItem(1, 1), createEmptyAnagramItem(2, 2)],
      );
      return;
    }

    const incomingExerciseName = String(gameInfo.exercise_name ?? "");
    const incomingSuggestion = String(gameInfo.suggestion ?? "");
    const incomingDescription = String(gameInfo.description ?? "");
    const incomingStatus = normalizeStatus(gameInfo.status);

    setExerciseName(incomingExerciseName);
    setSuggestion(incomingSuggestion);
    setDescription(incomingDescription);
    setExpectedTime(
      normalizeExpectedTime(
        (incomingRows[0] as Record<string, unknown> | undefined)?.expected_time,
      ),
    );
    setIsPublished(incomingStatus);
    initialExerciseNameRef.current = incomingExerciseName;
    initialSuggestionRef.current = incomingSuggestion;
    initialDescriptionRef.current = incomingDescription;

    const mappedItems =
      incomingRows.length > 0
        ? incomingRows.map((row, index) => {
            const record = row as Record<string, unknown>;
            const mappedId = Number(record.id ?? index + 1);
            const normalizedId =
              Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
            const wordRaw = String(record.word ?? record.question ?? "");
            const swapWordRaw: string[] = Array.isArray(record.swap_word)
              ? record.swap_word
              : buildSwapWord(
                  String(record.swap_word ?? record.answer ?? wordRaw),
                );
            const hintRaw = String(record.hint ?? "");
            const imageHintRaw = String(record.image_hint ?? "");
            const soundHintRaw = String(record.sound_hint ?? "");
            return {
              id: normalizedId,
              no: Number(record.no ?? index + 1),
              word: wordRaw,
              swapWord: swapWordRaw,
              imageWordFileName: getFileNameFromPath(
                String(record.image_word ?? ""),
              ),
              imageWordFile: null,
              imageWordUrl: buildUploadedMediaUrl(
                String(record.image_word ?? ""),
              ),
              audioWordFileName: getFileNameFromPath(
                String(record.sound_word ?? ""),
              ),
              audioWordFile: null,
              audioWordUrl: buildUploadedMediaUrl(
                String(record.sound_word ?? ""),
              ),
              imageSwapFileName: getFileNameFromPath(
                String(record.image_swap ?? ""),
              ),
              imageSwapFile: null,
              imageSwapUrl: buildUploadedMediaUrl(
                String(record.image_swap ?? ""),
              ),
              audioSwapFileName: getFileNameFromPath(
                String(record.sound_swap ?? ""),
              ),
              audioSwapFile: null,
              audioSwapUrl: buildUploadedMediaUrl(
                String(record.sound_swap ?? ""),
              ),
              hasHint: Boolean(hintRaw.trim() || imageHintRaw || soundHintRaw),
              hintText: hintRaw,
              hintImageFileName: getFileNameFromPath(imageHintRaw),
              hintImageFile: null,
              hintImageUrl: buildUploadedMediaUrl(imageHintRaw),
              hintAudioFileName: getFileNameFromPath(soundHintRaw),
              hintAudioFile: null,
              hintAudioUrl: buildUploadedMediaUrl(soundHintRaw),
            };
          })
        : [createEmptyAnagramItem(1, 1), createEmptyAnagramItem(2, 2)];

    const skillsFromGameInfo = extractGameSkillsFromUnknown({
      skill_listening_speaking: gameInfo.skill_listening_speaking,
      skill_lang_nature: gameInfo.skill_lang_nature,
      skill_reading: gameInfo.skill_reading,
      skill_writing: gameInfo.skill_writing,
    });

    let resolvedSkills = skillsFromGameInfo;
    if (incomingRows.length > 0) {
      const firstRow = incomingRows[0] as Record<string, unknown>;
      const firstRowSkills = extractGameSkillsFromRow(firstRow);
      resolvedSkills = firstRowSkills ?? { ...DEFAULT_GAME_SKILLS };

      if (!firstRowSkills) {
        console.warn(
          "[Anagram] Invalid skill values in first row, fallback to default false.",
        );
      }

      for (let index = 1; index < incomingRows.length; index += 1) {
        const rowRecord = incomingRows[index] as Record<string, unknown>;
        const rowSkills = extractGameSkillsFromRow(rowRecord);

        if (!rowSkills) {
          console.warn(
            `[Anagram] Invalid skill values in row ${index + 1}, using first row skill values.`,
          );
          continue;
        }

        if (!areGameSkillsEqual(rowSkills, resolvedSkills)) {
          console.warn(
            "[Anagram] Skill values are inconsistent across rows. Using the first row values.",
          );
          break;
        }
      }
    }

    setGameSkills(resolvedSkills);
    initialGameSkillsRef.current = resolvedSkills;

    const audiosFromGameInfo = extractGameAudiosFromUnknown({
      audio_question: gameInfo.audio_question,
      audio_choice: gameInfo.audio_choice,
      audio_assistant_voice: gameInfo.audio_assistant_voice,
      audio_background: gameInfo.audio_background,
    });

    let resolvedAudios = audiosFromGameInfo;
    if (incomingRows.length > 0) {
      const firstRow = incomingRows[0] as Record<string, unknown>;
      const firstRowAudios = extractGameAudiosFromRow(firstRow);
      resolvedAudios = firstRowAudios ?? { ...DEFAULT_GAME_AUDIOS };

      if (!firstRowAudios) {
        console.warn(
          "[Anagram] Invalid audio values in first row, fallback to default false.",
        );
      }

      for (let index = 1; index < incomingRows.length; index += 1) {
        const rowRecord = incomingRows[index] as Record<string, unknown>;
        const rowAudios = extractGameAudiosFromRow(rowRecord);

        if (!rowAudios) {
          console.warn(
            `[Anagram] Invalid audio values in row ${index + 1}, using first row audio values.`,
          );
          continue;
        }

        if (!areGameAudiosEqual(rowAudios, resolvedAudios)) {
          console.warn(
            "[Anagram] Audio values are inconsistent across rows. Using the first row values.",
          );
          break;
        }
      }
    }

    setGameAudios(resolvedAudios);
    initialGameAudiosRef.current = resolvedAudios;

    const normalizedItems = mappedItems.map((item, index) => ({
      ...item,
      no: index + 1,
    }));
    setItems(normalizedItems);

    initialAnagramIdsRef.current = collectPersistedIdsFromApiRows(
      incomingRows,
      (row) => {
        const mappedId = Number((row as Record<string, unknown>).id);
        return Number.isInteger(mappedId) && mappedId > 0 ? mappedId : null;
      },
    );
    nextAnagramIdRef.current =
      normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    setIsUpdateMode(incomingRows.length > 0);
    savedFormSnapshotRef.current = createAnagramItemsGameSnapshot(
      incomingExerciseName,
      incomingSuggestion,
      incomingDescription,
      resolvedSkills,
      resolvedAudios,
      normalizedItems,
    );
  };

  useEffect(() => {
    applyLoadedGameDetail(initialGameDetail);
  }, [initialGameDetail]);

  const setNoByOrder = (sourceItems: AnagramItem[]) =>
    sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

  const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
    setGameSkills((previous) => ({
      ...previous,
      [skillKey]: checked,
    }));
  };

  const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
    setGameAudios((previous) => ({
      ...previous,
      [audioKey]: checked,
    }));
  };

  const addItem = () => {
    const newId = nextAnagramIdRef.current;
    nextAnagramIdRef.current += 1;
    setItems((previous) =>
      setNoByOrder([
        ...previous,
        createEmptyAnagramItem(newId, previous.length + 1),
      ]),
    );
  };

  const removeItem = async (id: number) => {
    if (!canRemoveItem) {
      return;
    }
    if (isUpdateMode && initialAnagramIdsRef.current.has(id)) {
      const deleteResponse = await anagramAPI.hardDeleteAnagram({
        anagramId: id,
      });
      if (deleteResponse?.status === "failed") {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("deleteError"),
          type: "error",
        });
        return;
      }
      initialAnagramIdsRef.current.delete(id);
      patchSavedAnagramItemsSnapshotAfterItemRemoved(savedFormSnapshotRef, id);
    }
    setItems((previous) =>
      setNoByOrder(previous.filter((item) => item.id !== id)),
    );
  };

  const moveItem = (id: number, direction: -1 | 1) => {
    setItems((previous) => {
      const currentIndex = previous.findIndex((item) => item.id === id);
      if (currentIndex < 0) {
        return previous;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const reordered = [...previous];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, moved);
      return setNoByOrder(reordered);
    });
  };

  const updateItem = (
    id: number,
    updater: (item: AnagramItem) => AnagramItem,
  ) => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const updateTextField = (id: number, field: "word", value: string) => {
    const wordTarget = anagramItemFieldTarget(id, "word");
    const swapTarget = anagramItemFieldTarget(id, "swap");
    if (
      fieldErrorHighlightKey === wordTarget.highlightKey ||
      fieldErrorHighlightKey === swapTarget.highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(id, (item) => ({
      ...item,
      [field]: buildWord(value),
      swapWord: buildSwapWord(value),
    }));
  };

  const setFileOnItem = (
    id: number,
    options: {
      file: File;
      fileNameField:
        | "imageWordFileName"
        | "audioWordFileName"
        | "imageSwapFileName"
        | "audioSwapFileName"
        | "hintImageFileName"
        | "hintAudioFileName";
      fileField:
        | "imageWordFile"
        | "audioWordFile"
        | "imageSwapFile"
        | "audioSwapFile"
        | "hintImageFile"
        | "hintAudioFile";
      urlField:
        | "imageWordUrl"
        | "audioWordUrl"
        | "imageSwapUrl"
        | "audioSwapUrl"
        | "hintImageUrl"
        | "hintAudioUrl";
      forceEnableHint?: boolean;
    },
  ) => {
    updateItem(id, (item) => ({
      ...item,
      [options.fileNameField]: options.file.name,
      [options.fileField]: options.file,
      [options.urlField]: null,
      ...(options.forceEnableHint ? { hasHint: true } : {}),
    }));
  };

  const enableHint = (id: number) => {
    updateItem(id, (item) => ({ ...item, hasHint: true }));
  };

  const clearHint = (id: number) => {
    updateItem(id, (item) => ({
      ...item,
      hasHint: false,
      hintText: "",
      hintImageFileName: "",
      hintImageFile: null,
      hintImageUrl: null,
      hintAudioFileName: "",
      hintAudioFile: null,
      hintAudioUrl: null,
    }));
  };

  const getDeleteFieldLabel = (field: AnagramHardDeleteField) => {
    switch (field) {
      case "image_word":
        return tAnagram("wordImageLabel");
      case "sound_word":
        return tAnagram("wordAudioLabel");
      case "image_swap_word":
        return tAnagram("swapImageLabel");
      case "sound_swap_word":
        return tAnagram("swapAudioLabel");
      case "image_hint":
        return tAnagram("hintImageLabel");
      case "sound_hint":
        return tAnagram("hintAudioLabel");
      case "hint":
        return tAnagram("hint");
      default:
        return tAnagram("dataLabel");
    }
  };

  const requestDeleteMediaField = (
    itemId: number,
    field: AnagramHardDeleteField,
  ) => {
    setPendingMediaDelete({ itemId, field });
  };

  const clearMediaFieldFromItem = (
    itemId: number,
    field: AnagramHardDeleteField,
  ) => {
    if (field === "hint") {
      clearHint(itemId);
      return;
    }

    updateItem(itemId, (item) => {
      if (field === "image_word") {
        return {
          ...item,
          imageWordFileName: "",
          imageWordFile: null,
          imageWordUrl: null,
        };
      }

      if (field === "sound_word") {
        return {
          ...item,
          audioWordFileName: "",
          audioWordFile: null,
          audioWordUrl: null,
        };
      }

      if (field === "image_swap_word") {
        return {
          ...item,
          imageSwapFileName: "",
          imageSwapFile: null,
          imageSwapUrl: null,
        };
      }

      if (field === "sound_swap_word") {
        return {
          ...item,
          audioSwapFileName: "",
          audioSwapFile: null,
          audioSwapUrl: null,
        };
      }

      if (field === "image_hint") {
        return {
          ...item,
          hintImageFileName: "",
          hintImageFile: null,
          hintImageUrl: null,
        };
      }

      return {
        ...item,
        hintAudioFileName: "",
        hintAudioFile: null,
        hintAudioUrl: null,
      };
    });
  };

  const confirmDeleteMediaField = async () => {
    if (!pendingMediaDelete) {
      return;
    }

    const { itemId, field } = pendingMediaDelete;
    const shouldCallDeleteApi =
      isUpdateMode && initialAnagramIdsRef.current.has(itemId);

    try {
      setIsDeletingMedia(true);

      if (shouldCallDeleteApi) {
        const response = await anagramAPI.hardDeleteMediaFileAndHint({
          anagramId: itemId,
          field,
        });

        if (response?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("deleteFieldError", {
              field: getDeleteFieldLabel(field),
            }),
            type: "error",
          });
          return;
        }

        patchSavedAnagramItemsSnapshotAfterMediaDeleted(
          savedFormSnapshotRef,
          itemId,
          field,
        );
      }

      clearMediaFieldFromItem(itemId, field);
      setPendingMediaDelete(null);
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const collectUploadTargets = (sourceItems: AnagramItem[]) => {
    const targets: UploadTarget[] = [];

    sourceItems.forEach((item, index) => {
      const timestamp = Date.now();

      if (item.imageWordFile && !item.imageWordUrl) {
        const ext = getFileExtension(item.imageWordFile.name);
        targets.push({
          key: `a-${index}-image_word`,
          file: item.imageWordFile,
          fileName: `image_word_a${item.id}_${timestamp}${ext}`,
          sectionName: "image_word",
          refId: `anagram_${item.id}`,
        });
      }

      if (item.audioWordFile && !item.audioWordUrl) {
        const ext = getFileExtension(item.audioWordFile.name);
        targets.push({
          key: `a-${index}-sound_word`,
          file: item.audioWordFile,
          fileName: `sound_word_a${item.id}_${timestamp}${ext}`,
          sectionName: "sound_word",
          refId: `anagram_${item.id}`,
        });
      }

      if (item.imageSwapFile && !item.imageSwapUrl) {
        const ext = getFileExtension(item.imageSwapFile.name);
        targets.push({
          key: `a-${index}-image_swap_word`,
          file: item.imageSwapFile,
          fileName: `image_swap_word_a${item.id}_${timestamp}${ext}`,
          sectionName: "image_swap_word",
          refId: `anagram_${item.id}`,
        });
      }

      if (item.audioSwapFile && !item.audioSwapUrl) {
        const ext = getFileExtension(item.audioSwapFile.name);
        targets.push({
          key: `a-${index}-sound_swap_word`,
          file: item.audioSwapFile,
          fileName: `sound_swap_word_a${item.id}_${timestamp}${ext}`,
          sectionName: "sound_swap_word",
          refId: `anagram_${item.id}`,
        });
      }

      if (item.hintImageFile && !item.hintImageUrl) {
        const ext = getFileExtension(item.hintImageFile.name);
        targets.push({
          key: `a-${index}-image_hint`,
          file: item.hintImageFile,
          fileName: `image_hint_a${item.id}_${timestamp}${ext}`,
          sectionName: "image_hint",
          refId: `anagram_${item.id}`,
        });
      }

      if (item.hintAudioFile && !item.hintAudioUrl) {
        const ext = getFileExtension(item.hintAudioFile.name);
        targets.push({
          key: `a-${index}-sound_hint`,
          file: item.hintAudioFile,
          fileName: `sound_hint_a${item.id}_${timestamp}${ext}`,
          sectionName: "sound_hint",
          refId: `anagram_${item.id}`,
        });
      }
    });

    return targets;
  };

  const uploadPendingMedia = async (targets: UploadTarget[]) => {
    const uploadedUrlByKey = new Map<string, string>();

    if (targets.length === 0) {
      return uploadedUrlByKey;
    }

    const uploadResults = await Promise.allSettled(
      targets.map((target) =>
        uploadAPI
          .uploadMedia({
            file: target.file,
            fileName: target.fileName,
            sectionName: target.sectionName,
            ref_id: target.refId,
          })
          .then((response) => ({ target, response })),
      ),
    );

    for (const result of uploadResults) {
      if (result.status === "rejected") {
        throw new Error("upload_failed");
      }

      const response = result.value.response as {
        status?: string;
        url?: string;
      };
      if (response?.status === "failed" || !response?.url) {
        throw new Error("upload_failed");
      }

      uploadedUrlByKey.set(result.value.target.key, response.url);
    }

    return uploadedUrlByKey;
  };

  const applyUploadedUrlsToState = (
    sourceItems: AnagramItem[],
    uploadedUrlByKey: Map<string, string>,
  ) => {
    return sourceItems.map((item, index) => ({
      ...item,
      imageWordUrl:
        uploadedUrlByKey.get(`a-${index}-image_word`) ?? item.imageWordUrl,
      imageWordFile: uploadedUrlByKey.get(`a-${index}-image_word`)
        ? null
        : item.imageWordFile,
      audioWordUrl:
        uploadedUrlByKey.get(`a-${index}-sound_word`) ?? item.audioWordUrl,
      audioWordFile: uploadedUrlByKey.get(`a-${index}-sound_word`)
        ? null
        : item.audioWordFile,
      imageSwapUrl:
        uploadedUrlByKey.get(`a-${index}-image_swap_word`) ?? item.imageSwapUrl,
      imageSwapFile: uploadedUrlByKey.get(`a-${index}-image_swap_word`)
        ? null
        : item.imageSwapFile,
      audioSwapUrl:
        uploadedUrlByKey.get(`a-${index}-sound_swap_word`) ?? item.audioSwapUrl,
      audioSwapFile: uploadedUrlByKey.get(`a-${index}-sound_swap_word`)
        ? null
        : item.audioSwapFile,
      hintImageUrl:
        uploadedUrlByKey.get(`a-${index}-image_hint`) ?? item.hintImageUrl,
      hintImageFile: uploadedUrlByKey.get(`a-${index}-image_hint`)
        ? null
        : item.hintImageFile,
      hintAudioUrl:
        uploadedUrlByKey.get(`a-${index}-sound_hint`) ?? item.hintAudioUrl,
      hintAudioFile: uploadedUrlByKey.get(`a-${index}-sound_hint`)
        ? null
        : item.hintAudioFile,
    }));
  };

  const refreshFromServer = async () => {
    if (!previewUuid) {
      return;
    }

    const latestGameDetail =
      await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
    if (
      latestGameDetail &&
      (latestGameDetail as { status?: string }).status !== "failed"
    ) {
      applyLoadedGameDetail(
        latestGameDetail as FindTheAnagramProps["initialGameDetail"],
      );
    }
  };

  const handleSubmitAnagram = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tAnagram("error"),
        message: tAnagram("noRecentGameData"),
        type: "error",
      });
      return;
    }

    const wordValidationError = getAnagramWordValidationError(items, {
      minItemsRequired: tAnagram("minMatchItems"),
      wordRequiredByOrder: (no) => tAnagram("wordRequiredByOrder", { no }),
      wordMinLengthByOrder: (no, count) =>
        tAnagram("wordMinLengthByOrder", { no, count }),
    });
    if (wordValidationError) {
      setStatusPopUp({
        title: tAnagram("pleaseCheckData"),
        message: wordValidationError,
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const seenNos = new Set<number>();
      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        if (!Number.isInteger(no)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("invalidItemOrder"),
            type: "error",
          });
          return;
        }

        if (seenNos.has(no)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("duplicateItemOrder"),
            type: "error",
          });
          return;
        }
        seenNos.add(no);

        const normalizedSwapWord =
          item.swapWord.length > 0 ? item.swapWord : buildSwapWord(item.word);
        if (!Array.isArray(normalizedSwapWord)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("swapWordArrayByOrder", { no }),
            type: "error",
          });
          return;
        }

        if (
          !hasAtLeastOneMedia(
            item.word,
            item.imageWordUrl,
            item.audioWordUrl,
            item.imageWordFile,
            item.audioWordFile,
          )
        ) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tAnagram("error"),
              tAnagram("wordMediaRequiredByOrder", { no }),
              anagramItemFieldTarget(item.id, "word"),
            ),
          );
          return;
        }

        const swapText = normalizedSwapWord
          .map((entry) => String(entry))
          .join("")
          .trim();
        if (
          !hasAtLeastOneMedia(
            swapText,
            item.imageSwapUrl,
            item.audioSwapUrl,
            item.imageSwapFile,
            item.audioSwapFile,
          )
        ) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tAnagram("error"),
              tAnagram("swapMediaRequiredByOrder", { no }),
              anagramItemFieldTarget(item.id, "swap"),
            ),
          );
          return;
        }
      }

      const payloadRows = itemsWithResolvedUrls.map((item, index) => ({
        no: index + 1,
        expected_time: expectedTimeValue,
        word: buildWord(item.word),
        swap_word: normalizeSwapWord(item.swapWord, item.word),
        hint: item.hasHint ? item.hintText.trim() || null : null,
        image_word: item.imageWordUrl || null,
        sound_word: item.audioWordUrl || null,
        image_swap_word: item.imageSwapUrl || null,
        sound_swap_word: item.audioSwapUrl || null,
        image_hint: item.hasHint ? item.hintImageUrl || null : null,
        sound_hint: item.hasHint ? item.hintAudioUrl || null : null,
      }));

      const response = await anagramAPI.createAnagram({
        gameId: createdGameData.id,
        skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
        skill_lang_nature: normalizedGameSkills.skill_lang_nature,
        skill_reading: normalizedGameSkills.skill_reading,
        skill_writing: normalizedGameSkills.skill_writing,
        audio_question: normalizedGameAudios.audio_question,
        audio_choice: normalizedGameAudios.audio_choice,
        audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
        audio_background: normalizedGameAudios.audio_background,
        anagrams: payloadRows,
      });

      if (response?.status === "failed") {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("saveError"),
          type: "error",
        });
        return;
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      await refreshFromServer();
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tAnagram("anagram"), "create"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAnagram = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tAnagram("error"),
        message: tAnagram("noGameDataToUpdate"),
        type: "error",
      });
      return;
    }

    const wordValidationError = getAnagramWordValidationError(items, {
      minItemsRequired: tAnagram("minMatchItems"),
      wordRequiredByOrder: (no) => tAnagram("wordRequiredByOrder", { no }),
      wordMinLengthByOrder: (no, count) =>
        tAnagram("wordMinLengthByOrder", { no, count }),
    });
    if (wordValidationError) {
      setStatusPopUp({
        title: tAnagram("pleaseCheckData"),
        message: wordValidationError,
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tAnagram("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const changedSkillPayload: Partial<GameSkills> = {};
      if (
        normalizedGameSkills.skill_listening_speaking !==
        initialGameSkillsRef.current.skill_listening_speaking
      ) {
        changedSkillPayload.skill_listening_speaking =
          normalizedGameSkills.skill_listening_speaking;
      }
      if (
        normalizedGameSkills.skill_lang_nature !==
        initialGameSkillsRef.current.skill_lang_nature
      ) {
        changedSkillPayload.skill_lang_nature =
          normalizedGameSkills.skill_lang_nature;
      }
      if (
        normalizedGameSkills.skill_reading !==
        initialGameSkillsRef.current.skill_reading
      ) {
        changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
      }
      if (
        normalizedGameSkills.skill_writing !==
        initialGameSkillsRef.current.skill_writing
      ) {
        changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
      }

      const changedAudioPayload: Partial<GameAudios> = {};
      if (
        normalizedGameAudios.audio_question !==
        initialGameAudiosRef.current.audio_question
      ) {
        changedAudioPayload.audio_question =
          normalizedGameAudios.audio_question;
      }
      if (
        normalizedGameAudios.audio_choice !==
        initialGameAudiosRef.current.audio_choice
      ) {
        changedAudioPayload.audio_choice = normalizedGameAudios.audio_choice;
      }
      if (
        normalizedGameAudios.audio_assistant_voice !==
        initialGameAudiosRef.current.audio_assistant_voice
      ) {
        changedAudioPayload.audio_assistant_voice =
          normalizedGameAudios.audio_assistant_voice;
      }
      if (
        normalizedGameAudios.audio_background !==
        initialGameAudiosRef.current.audio_background
      ) {
        changedAudioPayload.audio_background =
          normalizedGameAudios.audio_background;
      }

      const seenNos = new Set<number>();
      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        if (!Number.isInteger(no)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("invalidItemOrder"),
            type: "error",
          });
          return;
        }

        if (seenNos.has(no)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("duplicateItemOrder"),
            type: "error",
          });
          return;
        }
        seenNos.add(no);

        const normalizedSwapWord =
          item.swapWord.length > 0 ? item.swapWord : buildSwapWord(item.word);
        if (!Array.isArray(normalizedSwapWord)) {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("swapWordArrayByOrder", { no }),
            type: "error",
          });
          return;
        }

        if (
          !hasAtLeastOneMedia(
            item.word,
            item.imageWordUrl,
            item.audioWordUrl,
            item.imageWordFile,
            item.audioWordFile,
          )
        ) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tAnagram("error"),
              tAnagram("wordMediaRequiredByOrder", { no }),
              anagramItemFieldTarget(item.id, "word"),
            ),
          );
          return;
        }

        const swapText = normalizedSwapWord
          .map((entry) => String(entry))
          .join("")
          .trim();
        if (
          !hasAtLeastOneMedia(
            swapText,
            item.imageSwapUrl,
            item.audioSwapUrl,
            item.imageSwapFile,
            item.audioSwapFile,
          )
        ) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tAnagram("error"),
              tAnagram("swapMediaRequiredByOrder", { no }),
              anagramItemFieldTarget(item.id, "swap"),
            ),
          );
          return;
        }
      }

      const currentIds = new Set<number>(
        itemsWithResolvedUrls.map((item) => item.id),
      );
      const deletedIds = Array.from(initialAnagramIdsRef.current).filter(
        (id) => !currentIds.has(id),
      );

      for (const deletedId of deletedIds) {
        const deleteResponse = await anagramAPI.hardDeleteAnagram({
          anagramId: deletedId,
        });
        if (deleteResponse?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("deleteItemByIdError", { id: deletedId }),
            type: "error",
          });
          return;
        }
      }

      const existingRows = itemsWithResolvedUrls
        .filter((item) => initialAnagramIdsRef.current.has(item.id))
        .map((item) => ({
          ...item,
          swapWord: normalizeSwapWord(item.swapWord, item.word),
        }));
      const newRows = itemsWithResolvedUrls
        .filter((item) => !initialAnagramIdsRef.current.has(item.id))
        .map((item) => ({
          ...item,
          swapWord: normalizeSwapWord(item.swapWord, item.word),
        }));

      for (const row of existingRows) {
        const updateResponse = await anagramAPI.updateAnagram({
          anagramId: row.id,
          no: row.no,
          expected_time: expectedTimeValue,
          word: buildWord(row.word),
          swap_word: normalizeSwapWord(row.swapWord, row.word),
          hint: row.hasHint ? row.hintText.trim() || null : null,
          image_word: row.imageWordUrl || null,
          sound_word: row.audioWordUrl || null,
          image_swap_word: row.imageSwapUrl || null,
          sound_swap_word: row.audioSwapUrl || null,
          image_hint: row.hasHint ? row.hintImageUrl || null : null,
          sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
          ...changedSkillPayload,
          ...changedAudioPayload,
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("updateItemByOrderError", { no: row.no }),
            type: "error",
          });
          return;
        }
      }

      if (newRows.length > 0) {
        const createNewRowsResponse = await anagramAPI.createAnagram({
          gameId: createdGameData.id,
          skill_listening_speaking:
            normalizedGameSkills.skill_listening_speaking,
          skill_lang_nature: normalizedGameSkills.skill_lang_nature,
          skill_reading: normalizedGameSkills.skill_reading,
          skill_writing: normalizedGameSkills.skill_writing,
          audio_question: normalizedGameAudios.audio_question,
          audio_choice: normalizedGameAudios.audio_choice,
          audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
          audio_background: normalizedGameAudios.audio_background,
          anagrams: newRows.map((row) => ({
            no: row.no,
            expected_time: expectedTimeValue,
            word: buildWord(row.word),
            swap_word: normalizeSwapWord(row.swapWord, row.word),
            hint: row.hasHint ? row.hintText.trim() || null : null,
            image_word: row.imageWordUrl || null,
            sound_word: row.audioWordUrl || null,
            image_swap_word: row.imageSwapUrl || null,
            sound_swap_word: row.audioSwapUrl || null,
            image_hint: row.hasHint ? row.hintImageUrl || null : null,
            sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
          })),
        });

        if (createNewRowsResponse?.status === "failed") {
          setStatusPopUp({
            title: tAnagram("error"),
            message: tAnagram("addItemError"),
            type: "error",
          });
          return;
        }
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      await refreshFromServer();

      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tAnagram("anagram"), "update"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseStatusPopUp = () => {
    closeStatusPopUp(setStatusPopUp);
  };

  const handleStatusButtonClick = () => {
    if (isStatusConfirming || isSubmitting) {
      return;
    }

    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tAnagram("updateStatusError"),
        message: tAnagram("noGameDataToChangeStatus"),
        type: "error",
      });
      return;
    }

    setIsStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setIsStatusConfirmOpen(false);
  };

  const confirmStatusToggle = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tAnagram("updateStatusError"),
        message: tAnagram("noGameDataToChangeStatus"),
        type: "error",
      });
      setIsStatusConfirmOpen(false);
      return;
    }

    const nextStatus = !isPublished;
    const dialogContent = getStatusToggleDialogContent(isPublished, {
      confirmPublish: tAnagram("confirmPublish"),
      publishPrompt: tAnagram("publishPrompt"),
      published: tAnagram("published"),
      publishedMessage: tAnagram("publishedMessage"),
      confirmUnpublish: tAnagram("confirmUnpublish"),
      unpublishPrompt: tAnagram("unpublishPrompt"),
      unpublished: tAnagram("unpublished"),
      unpublishedMessage: tAnagram("unpublishedMessage"),
    });

    setIsStatusConfirming(true);
    const response = await mainAPI.updateGameStatus({
      id: createdGameData.id,
      status: nextStatus,
    });
    setIsStatusConfirming(false);
    setIsStatusConfirmOpen(false);

    if (!response || response.status === "failed") {
      setStatusPopUp({
        title: tAnagram("updateStatusError"),
        message: tAnagram("changeStatusError"),
        type: "error",
      });
      return;
    }

    setIsPublished(nextStatus);
    setStatusPopUp({
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  const isExpectedTimeValid = useMemo(() => {
    const trimmed = expectedTime.trim();
    if (trimmed === "") {
      return false;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 0;
  }, [expectedTime]);
  const expectedTimeDisplay = useMemo(() => {
    if (!isExpectedTimeValid) {
      return "";
    }
    const value = Number(expectedTime.trim());
    if (value > 59) {
      const minutes = value / 60;
      const minuteText = Number.isInteger(minutes)
        ? String(minutes)
        : minutes.toFixed(2).replace(/\.?0+$/, "");
      return `${minuteText} ${tMain("expectedTimeMinuteUnit")}`;
    }
    return `${value} ${tMain("expectedTimeSecondUnit")}`;
  }, [expectedTime, isExpectedTimeValid, tMain]);
  const isSaveReady = useMemo(
    () => areAnagramItemsSaveReady(items) && isExpectedTimeValid,
    [items, isExpectedTimeValid],
  );

  const currentFormSnapshot = useMemo(
    () =>
      createAnagramItemsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        items,
      ),
    [exerciseName, suggestion, description, gameSkills, gameAudios, items],
  );
  const hasUnsavedChanges = useHasUnsavedGameFormChanges(
    isUpdateMode,
    currentFormSnapshot,
    savedFormSnapshotRef,
  );
  useReportGameEditorLeaveGuard(
    getShouldConfirmLeaveGameEditor(isUpdateMode, hasUnsavedChanges),
  );
  const canPreview = getCanGamePreview(
    isUpdateMode,
    previewUuid,
    hasUnsavedChanges,
  );

  const handleClickFinishButton = () => {
    if (isSubmitting || !isSaveReady) {
      return;
    }
    setIsConfirmSaveOpen(true);
  };

  const handleCancelConfirmSave = () => {
    setIsConfirmSaveOpen(false);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveOpen(false);
    if (isUpdateMode) {
      void handleUpdateAnagram();
      return;
    }
    void handleSubmitAnagram();
  };

  const handleOpenPreview = () => {
    if (!canPreview || hasUnsavedChanges || !isExpectedTimeValid) {
      if (!isExpectedTimeValid) {
        setStatusPopUp({
          title: tAnagram("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
      } else if (hasUnsavedChanges) {
        setStatusPopUp({
          title: PREVIEW_UNSAVED_TITLE,
          message: PREVIEW_UNSAVED_MESSAGE,
          type: "info",
        });
      }
      return;
    }

    if (!previewUuid) {
      setStatusPopUp({
        title: tAnagram("previewError"),
        message: tAnagram("noGameUUIDForPreview"),
        type: "error",
      });
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  const handleClosePreview = () => {
    setPreviewIframeUrl(null);
  };

  return (
    <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
      <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
        <span className="font-semibold text-[#4E4E4E]">
          {tAnagram("editContent")} &gt;{" "}
        </span>
        <span className="font-bold text-[#2b2b2b]">{tAnagram("anagram")}</span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
            {tAnagram("activityName")}
          </label>
          <input
            value={exerciseName}
            onChange={(event) => setExerciseName(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tAnagram("noActivityName")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tAnagram("details")}
          </label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tAnagram("selectCorrectAnswer")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tAnagram("questionTitle")}
          </label>
          <input
            value={suggestion}
            onChange={(event) => setSuggestion(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            // placeholder={tAnagram("dragAndMatch")}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-lg font-medium text-[#5b5b5b]">
            {tAnagram("status")}:
          </span>
          <button
            type="button"
            onClick={handleStatusButtonClick}
            disabled={
              !createdGameData?.id || isSubmitting || isStatusConfirming
            }
            className={getPublishButtonClass(isPublished)}
          >
            {renderPublishStatusContent(
              isPublished,
              {
                publish: tAnagram("publish"),
                unpublish: tAnagram("unpublish"),
              },
              16,
            )}
          </button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto p-3">
        <div className="min-w-[560px] space-y-3 rounded-md border border-[#e0e0e0] bg-[#f9f9f9] p-3">
          <div className="grid grid-cols-[40px_minmax(140px,1fr)_96px] items-center gap-4 pb-2">
            <span className="text-lg font-medium text-[#5a5a5a] text-center">
              {tAnagram("questionOrder")}
            </span>
            {/* <span className="inline-block rounded-md bg-[#fcee54] px-10 py-1.5 text-lg font-semibold text-[#333]">คำ</span>
                    <span className="text-lg font-medium text-[#5a5a5a] text-center">จัดการ</span> */}
          </div>

          <div className="space-y-3">
            {items.map((item, index) => {
              const wordImageId = `word-image-${item.id}`;
              const wordAudioId = `word-audio-${item.id}`;
              const hintImageId = `hint-image-${item.id}`;
              const hintAudioId = `hint-audio-${item.id}`;

              return (
                <div key={item.id} className="flex flex-col">
                  <div className="grid grid-cols-[40px_minmax(140px,1fr)_96px] min-w-[560px] items-center gap-4">
                    <span className="text-lg font-semibold text-[#5a5a5a] text-center">
                      {index + 1}.
                    </span>

                    <div
                      className="relative"
                      id={anagramItemFieldTarget(item.id, "swap").scrollId}
                    >
                      <div
                        id={anagramItemFieldTarget(item.id, "word").scrollId}
                        className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getGameFieldBorderClass(
                          isFieldError(
                            anagramItemFieldTarget(item.id, "word")
                              .highlightKey,
                          ) ||
                            isFieldError(
                              anagramItemFieldTarget(item.id, "swap")
                                .highlightKey,
                            ),
                        )}`}
                      >
                        <input
                          value={item.word}
                          onChange={(event) =>
                            updateTextField(item.id, "word", event.target.value)
                          }
                          className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                        />
                        <div className="flex items-center gap-1 pl-2">
                          <MediaEditGroup
                            imageInputId={wordImageId}
                            imageFile={item.imageWordFile}
                            imageUrl={item.imageWordUrl}
                            imageAlt={tAnagram("wordImageAlt")}
                            onImageChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "imageWordFileName",
                                fileField: "imageWordFile",
                                urlField: "imageWordUrl",
                              })
                            }
                            audioInputId={wordAudioId}
                            audioFile={item.audioWordFile}
                            audioUrl={item.audioWordUrl}
                            onAudioChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "audioWordFileName",
                                fileField: "audioWordFile",
                                urlField: "audioWordUrl",
                              })
                            }
                            onImageDelete={() =>
                              requestDeleteMediaField(item.id, "image_word")
                            }
                            onAudioDelete={() =>
                              requestDeleteMediaField(item.id, "sound_word")
                            }
                            disableDelete={isSubmitting || isDeletingMedia}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex w-full min-w-0 items-center justify-center gap-3 self-start">
                      <div className="flex flex-col items-center gap-0 text-[#555]">
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, -1)}
                          disabled={index === 0}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tAnagram("moveUp")}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, 1)}
                          disabled={index === items.length - 1}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tAnagram("moveDown")}
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteItemId(item.id)}
                        disabled={!canRemoveItem}
                        className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                        title={
                          canRemoveItem
                            ? tAnagram("delete")
                            : tAnagram("anagramMinItemsRequired", {
                                count: MIN_ITEMS,
                              })
                        }
                      >
                        <MdDeleteOutline size={22} />
                      </button>
                    </div>
                  </div>

                  {item.hasHint ? (
                    <div className="mt-2 grid grid-cols-[40px_minmax(140px,1fr)_96px] min-w-[560px] items-start gap-4">
                      <span></span>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-[#555]">
                          {tAnagram("hint")}
                        </label>
                        <div className="relative">
                          <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                            <input
                              value={item.hintText}
                              onChange={(event) =>
                                updateItem(item.id, (row) => ({
                                  ...row,
                                  hasHint: true,
                                  hintText: event.target.value,
                                }))
                              }
                              className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                              placeholder={tAnagram("typeHint")}
                            />
                            <div className="flex items-center gap-1 pl-2">
                              <MediaEditGroup
                                imageInputId={hintImageId}
                                imageFile={item.hintImageFile}
                                imageUrl={item.hintImageUrl}
                                imageAlt={tAnagram("hintImageAlt")}
                                onImageChange={(file) =>
                                  setFileOnItem(item.id, {
                                    file,
                                    fileNameField: "hintImageFileName",
                                    fileField: "hintImageFile",
                                    urlField: "hintImageUrl",
                                    forceEnableHint: true,
                                  })
                                }
                                audioInputId={hintAudioId}
                                audioFile={item.hintAudioFile}
                                audioUrl={item.hintAudioUrl}
                                onAudioChange={(file) =>
                                  setFileOnItem(item.id, {
                                    file,
                                    fileNameField: "hintAudioFileName",
                                    fileField: "hintAudioFile",
                                    urlField: "hintAudioUrl",
                                    forceEnableHint: true,
                                  })
                                }
                                onImageDelete={() =>
                                  requestDeleteMediaField(item.id, "image_hint")
                                }
                                onAudioDelete={() =>
                                  requestDeleteMediaField(item.id, "sound_hint")
                                }
                                disableDelete={isSubmitting || isDeletingMedia}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  requestDeleteMediaField(item.id, "hint")
                                }
                                className={MEDIA_BTN_DELETE_MD}
                                title={tAnagram("clearHint")}
                              >
                                <MdDeleteOutline size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <span></span>
                    </div>
                  ) : (
                    <div className="mt-2 grid grid-cols-[40px_minmax(140px,1fr)_96px] min-w-[560px] gap-4">
                      <span></span>
                      <button
                        type="button"
                        onClick={() => enableHint(item.id)}
                        className="cursor-pointer self-start text-left text-xs font-medium text-[#4ca2e2] hover:text-blue-600 hover:underline"
                      >
                        {tAnagram("addHint")}
                      </button>
                      <span></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 ml-3">
            <button
              type="button"
              onClick={addItem}
              className="ml-0 md:ml-10 rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] cursor-pointer"
            >
              {tAnagram("addItem")}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 w-full p-3">
        <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
          {tMain("expectedTimeLabel")}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={expectedTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setExpectedTime(nextValue);
              }
            }}
            required
            inputMode="numeric"
            className="h-10 w-full max-w-[360px] rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tMain("expectedTimePlaceholder")}
          />
          {expectedTimeDisplay ? (
            <span className="text-base font-semibold text-[#4a4a4a]">
              {expectedTimeDisplay}
            </span>
          ) : null}
        </div>
      </div>

      <div className="w-full p-3">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <TbCategory2 className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tAnagram("competency")}
                </div>
                <div className="text-sm text-[#888]">
                  {tAnagram("competencySubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <GiBrain className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tAnagram("listeningSpeakingThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_listening_speaking}
                  onChange={(event) =>
                    updateGameSkill(
                      "skill_listening_speaking",
                      event.target.checked,
                    )
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlineBookOpen className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tAnagram("readingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_reading}
                  onChange={(event) =>
                    updateGameSkill("skill_reading", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <MdOutlineChat className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tAnagram("understandingThaiLanguage")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_lang_nature}
                  onChange={(event) =>
                    updateGameSkill("skill_lang_nature", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlinePencilAlt className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tAnagram("writingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_writing}
                  onChange={(event) =>
                    updateGameSkill("skill_writing", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <HiOutlineVolumeUp className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tAnagram("sound")}
                </div>
                <div className="text-sm text-[#888]">
                  {tAnagram("soundSubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <HiOutlineMicrophone className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tAnagram("narration")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_question}
                      onChange={(event) =>
                        updateGameAudio("audio_question", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tAnagram("muteNarration")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_choice}
                      onChange={(event) =>
                        updateGameAudio("audio_choice", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tAnagram("muteOptionNarration")}
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <IoGameControllerOutline className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tAnagram("gameSound")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_assistant_voice}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_assistant_voice",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tAnagram("muteAssistant")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_background}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_background",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tAnagram("muteBackground")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        {isUpdateMode && previewUuid ? (
          <button
            type="button"
            onClick={handleOpenPreview}
            disabled={hasUnsavedChanges || isSubmitting || !isExpectedTimeValid}
            className={GAME_PREVIEW_BUTTON_CLASS}
            title={
              hasUnsavedChanges
                ? PREVIEW_BUTTON_DISABLED_TITLE
                : tAnagram("preview")
            }
          >
            {tAnagram("preview")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleClickFinishButton}
          disabled={isSubmitting || !isSaveReady}
          className="cursor-pointer rounded bg-[#f4d735] px-7 py-2 text-lg font-semibold text-[#4a4a4a] shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#e0c91a] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? tAnagram("saving") : tAnagram("save")}
        </button>
      </div>

      {isConfirmSaveOpen ? (
        <ConfirmPopUp
          title={tAnagram("confirmSave")}
          message={tAnagram("savePrompt")}
          cancelText={tAnagram("cancel")}
          confirmText={tAnagram("confirm")}
          onCancel={handleCancelConfirmSave}
          onConfirm={handleConfirmSave}
          isConfirming={isSubmitting}
        />
      ) : null}

      {isStatusConfirmOpen ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(isPublished, {
              confirmPublish: tAnagram("confirmPublish"),
              publishPrompt: tAnagram("publishPrompt"),
              published: tAnagram("published"),
              publishedMessage: tAnagram("publishedMessage"),
              confirmUnpublish: tAnagram("confirmUnpublish"),
              unpublishPrompt: tAnagram("unpublishPrompt"),
              unpublished: tAnagram("unpublished"),
              unpublishedMessage: tAnagram("unpublishedMessage"),
            }).confirmTitle
          }
          message={
            getStatusToggleDialogContent(isPublished, {
              confirmPublish: tAnagram("confirmPublish"),
              publishPrompt: tAnagram("publishPrompt"),
              published: tAnagram("published"),
              publishedMessage: tAnagram("publishedMessage"),
              confirmUnpublish: tAnagram("confirmUnpublish"),
              unpublishPrompt: tAnagram("unpublishPrompt"),
              unpublished: tAnagram("unpublished"),
              unpublishedMessage: tAnagram("unpublishedMessage"),
            }).confirmMessage
          }
          confirmText={tAnagram("confirm")}
          cancelText={tAnagram("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {pendingDeleteItemId !== null ? (
        <ConfirmPopUp
          title={tAnagram("confirmDelete")}
          message={tAnagram("deletePrompt")}
          cancelText={tAnagram("cancel")}
          confirmText={tAnagram("ok")}
          onCancel={() => setPendingDeleteItemId(null)}
          onConfirm={async () => {
            await removeItem(pendingDeleteItemId);
            setPendingDeleteItemId(null);
          }}
        />
      ) : null}

      {pendingMediaDelete !== null ? (
        <ConfirmPopUp
          title={tAnagram("confirmDelete")}
          message={tAnagram("deleteFieldPrompt", {
            field: getDeleteFieldLabel(pendingMediaDelete.field),
          })}
          cancelText={tAnagram("cancel")}
          confirmText={tAnagram("ok")}
          onCancel={() => {
            if (isDeletingMedia) {
              return;
            }
            setPendingMediaDelete(null);
          }}
          onConfirm={() => {
            void confirmDeleteMediaField();
          }}
          isConfirming={isDeletingMedia}
        />
      ) : null}

      {statusPopUp ? (
        <StatusPopUp
          title={statusPopUp.title}
          message={statusPopUp.message}
          type={statusPopUp.type}
          onClose={handleCloseStatusPopUp}
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle={tAnagram("previewTitle")}
          onClose={handleClosePreview}
          closeVariant="danger"
          shellVariant="editor"
        />
      ) : null}
    </section>
  );
}
